import React, { useContext, useEffect, useState, forwardRef } from "react";
import {
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  LabelList,
  Legend,
  Brush, // Import Brush for zoom functionality
} from "recharts";
import { InverterDetail2, MonthlySumApi, Tab4plantEnergyApi } from "../../../../../../../../api/Api";
import { Refresh } from "@mui/icons-material"; // Import icons
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress"; // Loading spinner
import { AuthenticationContext } from "../../../../../../../../context/AuthenticationContext";

const Tab4YearlyEnergy = forwardRef(function Tab4YearlyEnergy(props, ref) {
  const { LoggedInUserInfo } = useContext(AuthenticationContext);

  // Get the current year and current month (as YYYY-MM)
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  // Utility to format as YYYY-MM
  const formatYearMonth = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    return `${year}-${month}`;
  };

  // Start date: 12 months ago
  const getStartDate = () => {
    const now = new Date();
    now.setMonth(now.getMonth() - 12);
    return formatYearMonth(now);
  };

  // End date: current month
  const getEndDate = () => {
    const now = new Date();
    return formatYearMonth(now);
  };

  // In your component state
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());

  const [logData, setLogData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const credentials = {
    LOGIN_ID: localStorage.getItem("LoginID") || "",
    PASS_KEY: localStorage.getItem("KEY") || "xyz",
    LCODE: localStorage.getItem("LCODE") || "",
    MT1: startDate,
    MT2: endDate,
    TYP: "",
    WEB_KEY: "WEBXYZ321",
    FID: "",
  };

  const fetchLogData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await Tab4plantEnergyApi(credentials);
      if (res?.MONTH_SUM && Array.isArray(res.MONTH_SUM)) {
        setLogData(res.MONTH_SUM);
        const transformed = res.MONTH_SUM.map((item) => ({
          MONTH: item.MONTH,
          Plant_Total_KWH_Cumulative: parseFloat(item.Plant_Total_KWH_Cumulative) || 0,
          MONTH_Total_KWH: parseFloat(item.MONTH_Total_KWH) || 0,
          MAX: parseFloat(item.MAX) || 0,
          TARGET: parseFloat(item.Target) || 0

        }));
        setChartData(transformed);
      } else {
        setLogData([]);
        // console.error("Invalid response format");
      }
    } catch (err) {
      setError("Failed to fetch data");
      // console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchLogData();
  // }, [startDate, endDate]);

  // 1) StartDate / EndDate change → fetch immediately
  useEffect(() => {
    fetchLogData();
  }, [startDate, endDate]);

  // 2) Auto refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchLogData();
    }, 300000); // 5 minutes = 300000 ms

    return () => clearInterval(interval);
  }, []); // no dependencies



  // console.log("startdate &endDate",logData)

  return (
    <div ref={ref} className="row text-sm">
      <div className="col-md-12" style={{ height: "12rem", position: "relative" }}>

        {/* Chart */}
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </div>
        ) : (
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 0, right: 100, left: 60, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="MONTH" />

              {/* Left Y-Axis for Cumulative KWH */}
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke="red"
                label={{
                  value: "",
                  angle: -90,
                  position: "outsideLeft",
                  fill: "red",
                  offset: 10,
                }}
              />

              {/* Right Y-Axis shared for Bar and Line */}
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke={LoggedInUserInfo.dash_color}
                label={{
                  value: "",
                  angle: -90,
                  position: "outsideRight",
                  fill: LoggedInUserInfo.dash_color,
                  offset: 10,
                }}
              />

              <Tooltip />
              <Legend verticalAlign="top" height={36} />

              {/* Bar - Monthly Yield using right Y-axis */}
              <Bar
                yAxisId="right"
                dataKey="MONTH_Total_KWH"
                fill={LoggedInUserInfo.dash_color}
                name="Monthly Yield"
              />

              {/* Line - Highest using right Y-axis */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="MAX"
                stroke="green"
                name="Highest"
                strokeWidth={1.5}
              />

              {/* Line -Target using right Y-axis */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="TARGET"
                stroke="#000080"
                name="Target"
                strokeWidth={1.5}
                dot={{ r: 2 }}
              />


              {/* Line - Plant Cumulative using left Y-axis */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="Plant_Total_KWH_Cumulative"
                stroke="red"
                name="Plant Total Cumulative"
                strokeWidth={1.5}
              />

              {/* <Brush dataKey="MONTH" height={30} stroke="#8884d8" /> */}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>


  );
});

export default Tab4YearlyEnergy;
const containerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: "7px",
  border: "1px solid #25476A",
  padding: "2px",
  borderRadius: "7px"
};

const responsiveHeaderStyle = {
  fontWeight: "bold",
  margin: "0",
};

const responsiveControlsStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  justifyContent: "center",
};

const inputStyle = {
  padding: "3px 6px",
  borderRadius: "14px",
  border: "1px solid #ccc",
  marginLeft: "5px",
  fontSize: "10px",
};

const buttonStyle = {
  backgroundColor: "#007BFF",
  color: "#fff",
  border: "none",
  borderRadius: "14px",
  padding: "2px 9px",
  cursor: "pointer",
  fontSize: "10px",
};